/**
 * Bosh ekrandagi AYLANUVCHI lenta (TZ §10.8).
 *
 * Slaydlar ikki manbadan keladi:
 *   * bayram tabrigi — KLIENTDA hisoblanadi (`holidays.ts`), serverga
 *     bormaydi, chunki u faqat sanaga bog'liq;
 *   * banner       — serverdan (`/api/home` -> `banners`): reklama
 *     (`ad`) va o'z e'lonlarimiz (`promo`).
 *
 * Bayram BIRINCHI turadi. Ilova ochilganda odam avval tabrikni ko'radi,
 * 7.5 soniyadan keyin lenta yon tomonga suriladi va reklama chiqadi.
 *
 * NEGA HARAKAT TO'XTAYDI
 * ----------------------
 * WCAG 2.2.2 (A daraja): 5 soniyadan uzoq davom etadigan avtomatik
 * harakat to'xtatilishi mumkin bo'lishi SHART. Bu yerda uchta to'xtash
 * yo'li bor:
 *   1. odam nuqtaga bossa yoki lentaga tegsa — aylanish butunlay
 *      to'xtaydi (u endi o'zi boshqaradi);
 *   2. ilova fonga o'tsa — `visibilitychange` to'xtatadi (batareya);
 *   3. `prefers-reduced-motion` — aylanish umuman boshlanmaydi.
 *
 * NEGA SVAYP YO'Q
 * ---------------
 * Telegram Mini App da gorizontal svayp tizimning o'z ishoralari bilan
 * (orqaga qaytish, oynani yopish) to'qnashadi. Nuqtalar bir xil
 * boshqaruvni beradi va hech narsa bilan urishmaydi.
 *
 * "REKLAMA" YORLIG'I MAJBURIY
 * ---------------------------
 * `kind === "ad"` bo'lgan slaydda yorliq DOIM ko'rinadi va uni
 * o'chirish yo'li yo'q. O'zbekiston "Reklama to'g'risida"gi qonuni
 * reklamani ajratib ko'rsatishni talab qiladi; bundan tashqari
 * mahsulotning butun qiymati ishonchda — pullik blok grant bilan
 * adashtirilsa, u ishonch bir marta va butunlay yo'qoladi.
 */

import { useEffect, useRef, useState } from "react";
import {
  FLAG_BLUE, FLAG_GREEN, FLAG_RED, FLAG_WHITE, TULIP, crescent, star,
} from "./holiday-decor";
import type { Holiday } from "./holidays";
import { t, tx } from "./i18n";
import { IconMegaphone, IconSparkle } from "./icons";
import { openExternal } from "./telegram";
import type { BannerSlide } from "./types";

/** Slayd almashish oralig'i. */
const ROTATE_MS = 7500;

/* ------------------------------------------------------------- kontrast */

/**
 * Urg'u rangi ikonka va qirraga tushadi — unga shisha fon ustida
 * kamida 3:1 kerak (WCAG, matn bo'lmagan element).
 *
 * Reklama beruvchining brend rangi ko'pincha quyuq bo'ladi (masalan
 * to'q ko'k `#1a3a8f`) va bu talabni bajarmaydi. Bunday rang
 * QABUL QILINMAYDI — lenta odatdagi oltin urg'uda qoladi. Aks holda
 * reklama ilovaning yagona o'lchanmagan joyi bo'lib qolardi.
 *
 * `GLASS_L` — eng yorug' shisha yuzaning yorug'ligi. U to'g'ridan-to'g'ri
 * o'lchanmaydi (gradient + shaffoflik + blur yig'indisi), shuning uchun
 * ma'lum nuqtadan teskari hisoblangan: styles.css da oltin ikonka shu
 * fon ustida 4.01:1 deb yozilgan.
 */
const GLASS_L = (luminance("#f5b921") + 0.05) / 4.01 - 0.05;

function luminance(hex: string): number {
  const ch = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/**
 * Xavfsiz urg'u rangi yoki `undefined` (u holda odatdagi rang qoladi).
 *
 * Shakl ham tekshiriladi: qiymat CSS o'zgaruvchisiga to'g'ridan-to'g'ri
 * qo'yiladi, ya'ni bu yer xavfsizlik chegarasi. Serverda ham xuddi shu
 * cheklov bor (`banner_accent_hex`), lekin ikki tomon ham tekshiradi —
 * mock rejimi va kelajakdagi boshqa manbalar server orqali o'tmaydi.
 */
export function safeAccent(accent: string | null | undefined): string | undefined {
  if (!accent || !/^#[0-9a-f]{6}$/i.test(accent)) return undefined;
  const ratio = (luminance(accent.toLowerCase()) + 0.05) / (GLASS_L + 0.05);
  return ratio >= 3 ? accent.toLowerCase() : undefined;
}

/* --------------------------------------------------------------- shakl */

/** Lentaning chap tomonidagi ikonka. */
function Mark({ theme }: { theme: string }) {
  if (theme === "independence") {
    return (
      <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true" focusable="false">
        <g fill="currentColor">
          <path d={crescent(12, 16, 9)} fillRule="evenodd" />
          <path d={star(23, 10, 4)} />
          <path d={star(26, 19, 3.2)} />
          <path d={star(20, 24, 3.2)} />
        </g>
      </svg>
    );
  }
  if (theme === "eid") {
    return (
      <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true" focusable="false">
        <g fill="currentColor">
          <path d={crescent(14, 16, 10)} fillRule="evenodd" />
          <path d={star(25, 11, 5)} />
        </g>
      </svg>
    );
  }
  if (theme === "navruz") {
    return (
      <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true" focusable="false">
        <g transform="translate(16 28)" fill="currentColor">
          <path d={TULIP} transform="scale(0.82)" />
          <path d="M0 -2V2" stroke="currentColor" strokeWidth="2.4" />
          <path d="M0 -1C-7 -4-10 -10-9 -15C-4 -12-1.6 -6 0 -1Z" opacity="0.75" />
        </g>
      </svg>
    );
  }
  if (theme === "winter") {
    return (
      <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true" focusable="false">
        <g transform="translate(16 16)" fill="none" stroke="currentColor"
          strokeWidth="1.9" strokeLinecap="round">
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <path key={a} transform={`rotate(${a})`}
              d="M0 0V-13M0 -5.5l-3.2 -3.2M0 -5.5l3.2 -3.2M0 -9.6l-2.4 -2.4M0 -9.6l2.4 -2.4" />
          ))}
        </g>
      </svg>
    );
  }
  // Banner: reklama — karnay, o'z e'lonimiz — uchqun
  return theme === "ad" ? <IconMegaphone size={21} /> : <IconSparkle size={21} />;
}

/** O'ng qirradagi naqsh — lentani oddiy xabar qutisidan ajratadi. */
function Motif({ theme }: { theme: string }) {
  if (theme === "independence") {
    return (
      <svg className="ribbon-motif" viewBox="0 0 120 64" aria-hidden="true" focusable="false">
        <g fill="none" strokeWidth="13" transform="translate(0 6)">
          <path d="M0 12Q30 2 60 12T120 12" stroke={FLAG_BLUE} opacity="0.5" />
          <path d="M0 26Q30 16 60 26T120 26" stroke={FLAG_WHITE} opacity="0.42" />
          <path d="M0 40Q30 30 60 40T120 40" stroke={FLAG_GREEN} opacity="0.5" />
        </g>
        <g fill="none" stroke={FLAG_RED} strokeWidth="1.6" opacity="0.7" transform="translate(0 6)">
          <path d="M0 19Q30 9 60 19T120 19" />
          <path d="M0 33Q30 23 60 33T120 33" />
        </g>
      </svg>
    );
  }
  if (theme === "eid") {
    return (
      <svg className="ribbon-motif" viewBox="0 0 120 64" aria-hidden="true" focusable="false">
        <g fill="none" stroke="currentColor" strokeWidth="1.1" opacity="0.45">
          {[18, 58, 98].map((x) => (
            <g key={x} transform={`translate(${x} 32)`}>
              <rect x="-13" y="-13" width="26" height="26" />
              <rect x="-13" y="-13" width="26" height="26" transform="rotate(45)" />
              <circle r="3" />
            </g>
          ))}
        </g>
      </svg>
    );
  }
  if (theme === "navruz") {
    return (
      <svg className="ribbon-motif" viewBox="0 0 120 64" aria-hidden="true" focusable="false">
        <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" opacity="0.5">
          <path d="M-6 46C16 46 20 14 40 14C56 14 58 42 76 42C94 42 98 18 124 18" />
          <path d="M34 22C28 16 28 8 34 4C38 10 38 18 34 22Z" />
          <path d="M80 36C86 30 86 22 80 18C76 24 76 32 80 36Z" />
        </g>
      </svg>
    );
  }
  if (theme === "winter") {
    return (
      <svg className="ribbon-motif" viewBox="0 0 120 64" aria-hidden="true" focusable="false">
        <g fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.45">
          {[[22, 20, 1], [62, 40, 1.35], [98, 16, 0.85]].map(([x, y, s], i) => (
            <g key={i} transform={`translate(${x} ${y}) scale(${s})`}>
              {[0, 60, 120, 180, 240, 300].map((a) => (
                <path key={a} transform={`rotate(${a})`}
                  d="M0 0V-12M0 -5l-3 -3M0 -5l3 -3M0 -9l-2.2 -2.2M0 -9l2.2 -2.2" />
              ))}
            </g>
          ))}
        </g>
      </svg>
    );
  }
  // Banner uchun neytral naqsh — konsentrik yoylar
  return (
    <svg className="ribbon-motif" viewBox="0 0 120 64" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.4">
        {[16, 30, 44, 58].map((r) => (
          <circle key={r} cx="104" cy="32" r={r} />
        ))}
      </g>
    </svg>
  );
}

/* --------------------------------------------------------------- slayd */

interface Slide {
  key: string;
  /** Naqsh va ikonka tanlash uchun: bayram mavzusi yoki banner turi. */
  theme: string;
  title: string;
  note: string | null;
  url?: string | null;
  accent?: string | undefined;
  /** `true` bo'lsa "Reklama" yorlig'i ko'rsatiladi. */
  ad?: boolean;
  bannerId?: number;
}

function slidesOf(holiday: Holiday | null, banners: BannerSlide[]): Slide[] {
  const out: Slide[] = [];
  // Bayram BIRINCHI: ilova ochilganda odam avval tabrikni ko'radi.
  if (holiday) {
    out.push({
      key: `holiday-${holiday.key}`,
      theme: holiday.theme,
      // Matn LUG'ATDAN — bayram jadvalida faqat sana va til bor.
      title: tx("holiday", `${holiday.key}.greeting`),
      note: tx("holiday", `${holiday.key}.note`),
    });
  }
  for (const b of banners) {
    out.push({
      key: `banner-${b.id}`,
      theme: b.kind,
      title: b.title,
      note: b.note,
      url: b.url,
      accent: safeAccent(b.accent),
      ad: b.kind === "ad",
      bannerId: b.id,
    });
  }
  return out;
}

function SlideBody({ slide }: { slide: Slide }) {
  return (
    <>
      <span className="ribbon-icon">
        <Mark theme={slide.theme} />
      </span>
      <span className="ribbon-text">
        <b>{slide.title}</b>
        {slide.note ? <span className="ribbon-note">{slide.note}</span> : null}
      </span>
      {slide.ad ? <span className="ribbon-tag">{t("ribbon.ad")}</span> : null}
      <Motif theme={slide.theme} />
    </>
  );
}

/* --------------------------------------------------------------- lenta */

export function Ribbon({
  holiday,
  banners = [],
  onEvent,
}: {
  holiday: Holiday | null;
  banners?: BannerSlide[];
  /** Ko'rsatish va bosishni yozadi — busiz reklamani sotib bo'lmaydi. */
  onEvent?: (kind: "banner_view" | "banner_click", bannerId: number) => void;
}) {
  const slides = slidesOf(holiday, banners);
  const [index, setIndex] = useState(0);
  // Odam o'zi boshqarganidan keyin avtomatik aylanish QAYTMAYDI.
  const [auto, setAuto] = useState(true);
  const seen = useRef<Set<number>>(new Set());

  const count = slides.length;

  useEffect(() => {
    if (count < 2 || !auto) return;
    // Harakatni kamaytirish so'ralgan bo'lsa aylanish umuman boshlanmaydi
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const tick = () => {
      // Ilova fonda bo'lsa slayd almashmaydi: odam uni ko'rmaydi, ya'ni
      // reklama "ko'rsatildi" deb hisoblanishi ham noto'g'ri bo'lardi.
      if (document.visibilityState === "visible") {
        setIndex((i) => (i + 1) % count);
      }
    };
    const id = window.setInterval(tick, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [count, auto]);

  // Ko'rsatish HAR SLAYD uchun bir marta yoziladi
  useEffect(() => {
    const slide = slides[index];
    if (!slide?.bannerId || seen.current.has(slide.bannerId)) return;
    seen.current.add(slide.bannerId);
    onEvent?.("banner_view", slide.bannerId);
  }, [index, slides, onEvent]);

  if (!count) return null;

  const stop = () => setAuto(false);

  const open = (slide: Slide) => {
    stop();
    if (slide.bannerId) onEvent?.("banner_click", slide.bannerId);
    if (slide.url) openExternal(slide.url);
  };

  return (
    <div
      className="ribbon"
      role="region"
      aria-label={t("ribbon.label")}
      aria-roledescription="karusel"
    >
      <div
        className="ribbon-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((slide, i) => {
          const hidden = i !== index;
          const common = {
            className: `ribbon-slide${slide.ad ? " ribbon-ad" : ""}`,
            // Ko'rinmayotgan slayd klaviatura va o'quvchi uchun ham yo'q
            "aria-hidden": hidden || undefined,
            style: slide.accent
              ? ({ "--accent": slide.accent } as React.CSSProperties)
              : undefined,
          };
          return slide.url ? (
            <button
              key={slide.key}
              type="button"
              {...common}
              tabIndex={hidden ? -1 : undefined}
              onClick={() => open(slide)}
            >
              <SlideBody slide={slide} />
            </button>
          ) : (
            <div key={slide.key} {...common} role="note">
              <SlideBody slide={slide} />
            </div>
          );
        })}
      </div>

      {count > 1 ? (
        <div className="ribbon-dots">
          {slides.map((slide, i) => (
            <button
              key={slide.key}
              className={`ribbon-dot${i === index ? " ribbon-dot-on" : ""}`}
              type="button"
              aria-label={`${i + 1}-slayd`}
              aria-current={i === index ? "true" : undefined}
              onClick={() => {
                stop();
                setIndex(i);
              }}
            >
              <i />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
